import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Carousel } from '@mantine/carousel';
import type { EmblaCarouselType } from 'embla-carousel';
import { Alert, Box, Button, Center, Loader, Modal, Stack, Text } from '@mantine/core';
import { useApp } from '../../../../context/AppContext';
import {
  cancelBooking,
  confirmBooking,
  declineBooking,
  getBookingsByIds,
} from '../../../../services/BookingService';
import { groupBookingsForDecision } from '../../../../utils/bookingGrouping';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { BookingDecisionCard, BookingDecision } from './BookingDecisionCard';
import { primaryColor } from '../../../../constants/theme';
import classes from './BookingDecisionCarousel.module.css';

interface BookingDecisionModalProps {
  opened: boolean;
  onClose: () => void;
  bookingIds: number[];
}

const getInitialDecision = (booking: BookingResponseModel): BookingDecision => {
  if (booking.isCancelled) return 'Cancelled';
  if (booking.rejected) return 'Rejected';
  if (booking.accepted) return 'Accepted';
  return null;
};

const getSummaryMessage = (
  groups: BookingResponseModel[][],
  decisions: Record<string, BookingDecision>
): string => {
  const now = new Date();
  const pending = groups.filter(
    (g) => decisions[g[0].id] === null && new Date(g[0].startIso) >= now
  ).length;
  const total = groups.filter((g) => new Date(g[0].startIso) >= now).length;

  if (pending > 0) {
    return `${pending} of ${total} booking${total > 1 ? 's' : ''} pending your approval.`;
  }

  const values = Object.values(decisions);
  if (values.length > 0 && values.every((v) => v === 'Accepted')) {
    return 'All bookings accepted.';
  }
  if (values.length > 0 && values.every((v) => v === 'Rejected')) {
    return 'All bookings rejected.';
  }
  if (values.length > 0 && values.every((v) => v === 'Cancelled')) {
    return 'All bookings cancelled.';
  }
  return 'All bookings reviewed.';
};

export const BookingDecisionModal = ({ opened, onClose, bookingIds }: BookingDecisionModalProps) => {
  const { token, profile } = useApp();

  const [decisions, setDecisions] = useState<Record<string, BookingDecision>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const emblaApiRef = useRef<EmblaCarouselType | null>(null);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    data: groups,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['booking-decision-modal', bookingIds.join(',')],
    enabled: opened && bookingIds.length > 0 && !!token,
    queryFn: async () => {
      const response = await getBookingsByIds({ bookingIds }, token ?? '');
      return groupBookingsForDecision(response.bookings ?? []);
    },
  });

  useEffect(() => {
    if (!groups) return;
    const initial: Record<string, BookingDecision> = {};
    for (const group of groups) {
      initial[group[0].id] = getInitialDecision(group[0]);
    }
    setDecisions(initial);
  }, [groups]);

  // Reset state when modal closes
  useEffect(() => {
    if (!opened) {
      setDecisions({});
      setLoadingStates({});
      setActionError(null);
    }
  }, [opened]);

  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
    };
  }, []);

  const setDecision = useCallback((groupId: number, decision: BookingDecision) => {
    setDecisions((prev) => ({ ...prev, [groupId]: decision }));
  }, []);

  const setGroupLoading = useCallback((groupId: number, loading: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [groupId]: loading }));
  }, []);

  const advanceToNextPending = useCallback(
    (currentGroupId: number) => {
      if (!groups || !emblaApiRef.current) return;
      const currentIndex = groups.findIndex((g) => g[0].id === currentGroupId);
      const nextPendingIndex = groups.findIndex(
        (g, i) => i > currentIndex && decisions[g[0].id] === null
      );
      if (nextPendingIndex !== -1) {
        autoAdvanceTimerRef.current = setTimeout(() => {
          emblaApiRef.current?.scrollTo(nextPendingIndex);
        }, 500);
      }
    },
    [groups, decisions]
  );

  const { mutate: acceptMutate } = useMutation({
    mutationFn: async (group: BookingResponseModel[]) => {
      await confirmBooking(
        { bookingIds: group.map((b) => b.id), userId: group[0].userId },
        token ?? ''
      );
    },
    onSuccess: (_data, group) => {
      setDecision(group[0].id, 'Accepted');
      setGroupLoading(group[0].id, false);
      advanceToNextPending(group[0].id);
    },
    onError: (_error, group) => {
      setGroupLoading(group[0].id, false);
      setActionError('Failed to accept booking. Please try again.');
    },
  });

  const { mutate: rejectMutate } = useMutation({
    mutationFn: async (group: BookingResponseModel[]) => {
      await declineBooking({ bookingIds: group.map((b) => b.id) }, token ?? '');
    },
    onSuccess: (_data, group) => {
      setDecision(group[0].id, 'Rejected');
      setGroupLoading(group[0].id, false);
      advanceToNextPending(group[0].id);
    },
    onError: (_error, group) => {
      setGroupLoading(group[0].id, false);
      setActionError('Failed to reject booking. Please try again.');
    },
  });

  const { mutate: cancelMutate } = useMutation({
    mutationFn: async (group: BookingResponseModel[]) => {
      await cancelBooking(
        { bookingIds: group.map((b) => b.id), cancellationReason: 'Cancelled by professional' },
        token ?? ''
      );
    },
    onSuccess: (_data, group) => {
      setDecision(group[0].id, 'Cancelled');
      setGroupLoading(group[0].id, false);
    },
    onError: (_error, group) => {
      setGroupLoading(group[0].id, false);
      setActionError('Failed to cancel booking. Please try again.');
    },
  });

  const handleAccept = (group: BookingResponseModel[]) => {
    setActionError(null);
    setGroupLoading(group[0].id, true);
    acceptMutate(group);
  };

  const handleReject = (group: BookingResponseModel[]) => {
    setActionError(null);
    setGroupLoading(group[0].id, true);
    rejectMutate(group);
  };

  const handleCancel = (group: BookingResponseModel[]) => {
    setActionError(null);
    setGroupLoading(group[0].id, true);
    cancelMutate(group);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Center p="xl">
          <Loader data-testid="booking-decision-modal-loader" />
        </Center>
      );
    }

    if (isError || !groups) {
      return (
        <Center p="xl">
          <Stack align="center" gap="md">
            <Text c="red" data-testid="booking-decision-modal-error">
              Failed to load bookings. Please try again.
            </Text>
            <Button color={primaryColor} onClick={() => refetch()} data-testid="booking-decision-modal-retry">
              Retry
            </Button>
          </Stack>
        </Center>
      );
    }

    if (groups.length === 0) {
      return (
        <Center p="xl">
          <Text c="dimmed" data-testid="booking-decision-modal-empty">
            No bookings found.
          </Text>
        </Center>
      );
    }

    const summaryMessage = getSummaryMessage(groups, decisions);

    return (
      <Stack gap="lg">
        <Text c="dimmed" ta="left" data-testid="booking-decision-modal-summary">
          {summaryMessage}
        </Text>
        {actionError && (
          <Alert
            color="red"
            withCloseButton
            onClose={() => setActionError(null)}
            data-testid="booking-decision-modal-action-error"
          >
            {actionError}
          </Alert>
        )}
        <Box w="100%">
          <Carousel
            withIndicators
            withControls={groups.length > 1}
            loop={false}
            slideSize="100%"
            slideGap="md"
            align="start"
            getEmblaApi={(api) => { emblaApiRef.current = api; }}
            classNames={{ indicator: classes.indicator, indicators: classes.indicators }}
            styles={{
              slide: { display: 'flex', alignItems: 'stretch', width: '100%' },
              controls: {
                position: 'relative',
                inset: 'unset',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 8,
                paddingLeft: 0,
                paddingRight: 0,
              },
              control: {
                position: 'static',
                transform: 'none',
                width: 44,
                height: 44,
                borderRadius: 6,
                boxShadow: 'none',
              },
            }}
          >
            {groups.map((group) => (
              <Carousel.Slide key={group[0].id}>
                <BookingDecisionCard
                  group={group}
                  decision={decisions[group[0].id] ?? null}
                  isLoading={loadingStates[group[0].id] ?? false}
                  profileId={profile?.id ?? ''}
                  onAccept={() => handleAccept(group)}
                  onReject={() => handleReject(group)}
                  onCancel={() => handleCancel(group)}
                />
              </Carousel.Slide>
            ))}
          </Carousel>
        </Box>
      </Stack>
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Booking Requests"
      centered
      size="lg"
    >
      {renderContent()}
    </Modal>
  );
};
